import React from 'react';
import { RouteContext } from 'app/src/router';
import { BoardTableViewFilter } from './filters/BoardTableViewFilter';
import { DueFilter } from './filters/DueFilter';
import { MembersFilter } from './filters/MembersFilter';
import { ListFilter } from './filters/ListFilter';
import { LabelsFilter } from './filters/LabelsFilter';
import { BoardsFilter } from './filters/BoardsFilter';

export interface ViewFilters extends Record<string, BoardTableViewFilter> {
  boards: BoardsFilter;
  due: DueFilter;
  members: MembersFilter;
  list: ListFilter;
  labels: LabelsFilter;
}

export const filterTypes: {
  name: keyof ViewFilters;
  Filter: new () => BoardTableViewFilter;
}[] = [
  { name: 'boards', Filter: BoardsFilter },
  { name: 'due', Filter: DueFilter },
  { name: 'members', Filter: MembersFilter },
  { name: 'list', Filter: ListFilter },
  { name: 'labels', Filter: LabelsFilter },
];

export interface ViewFiltersSourceBase {
  filters: ViewFilters;
  editable: boolean;
}

export interface ViewFiltersSourceEditable extends ViewFiltersSourceBase {
  editable: true;
  setFilter: (filter: BoardTableViewFilter) => void;
  clearNonBoardFilters: () => void;
}

export interface ViewFiltersSourceReadOnly extends ViewFiltersSourceBase {
  editable: false;
}

export type ViewFiltersSource =
  | ViewFiltersSourceEditable
  | ViewFiltersSourceReadOnly;

export const getEmptyViewFilters = (): ViewFilters =>
  Object.fromEntries(
    filterTypes.map(({ name, Filter }) => [name, new Filter()]),
  ) as ViewFilters;

export interface ViewFiltersContextValue<T extends ViewFiltersSourceBase> {
  viewFilters: T;
}

export const ViewFiltersContext = React.createContext<
  ViewFiltersContextValue<ViewFiltersSourceEditable | ViewFiltersSourceReadOnly>
>({
  viewFilters: {
    // eslint-disable-next-line @trello/no-module-logic
    filters: getEmptyViewFilters(),
    editable: false,
  },
});

export const constructNewUrl = function (
  routeContext: RouteContext,
): ((viewFilters: ViewFilters) => URL) | undefined {
  if (!routeContext.url) {
    return undefined;
  }
  const newUrl = new URL(routeContext.url.toString());

  return function constructUrlFilterParams(viewFilters: ViewFilters) {
    for (const [, filter] of Object.entries(viewFilters)) {
      const newUrlParams = filter.toUrlParams();

      for (const [key, value] of Object.entries(newUrlParams)) {
        if (!value) {
          newUrl.searchParams.delete(key);
        } else {
          newUrl.searchParams.set(key, value);
        }
      }
    }
    newUrl.search = newUrl.search.replace(/%2C/g, ',').replace(/%3A/g, ':');
    return newUrl;
  };
};
